import * as THREE from 'three';
import type { City, Place } from '../world/city';
import type { BusStop } from '../world/street-transit';
import type { PhysicsCity } from '../physics/world';
import type { Population, Person, TrafficVehicle } from './population';
import { Reservations } from './reservations';
interface Bus {
  car: TrafficVehicle;
  stop: number;
  next: number;
  state: 'driving' | 'dwell' | 'closing' | 'closed' | 'wreck';
  remaining: number;
  retry: number;
  reason: string;
  visits: number;
}
interface BusTicket {
  person: Person;
  origin: BusStop;
  destination: BusStop;
  queue: Place;
  final: Place;
  state: 'approaching' | 'waiting' | 'boarding' | 'aboard' | 'alighting';
  bus?: Bus;
  time: number;
  route: number[];
}
/** A finite fleet shares normal collision-aware traffic. Boarding preserves the
 * resident, and damage releases the original passenger into the same incident. */
export class Buses {
  fleet: Bus[] = [];
  tickets = new Map<string, BusTicket>();
  log: { time: number; action: string; bus?: string; person?: string; stop?: string }[] = [];
  readonly reservations: Reservations;
  time = 0;
  boardings = 0;
  alightings = 0;
  disruptions = 0;
  private epoch = 0;
  private checked = 0;
  private lastDeparture = new Map<string, number>();
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.reservations = new Reservations(() => physics.life.epoch);
    this.reset();
  }
  get stops(): BusStop[] {
    return this.city.streetTransit?.stops ?? [];
  }
  private record(action: string, bus?: Bus, person?: Person, stop?: BusStop) {
    this.log.push({ time: this.time, action, bus: bus?.car.id, person: person?.id, stop: stop?.id });
    if (this.log.length > 384) this.log.shift();
  }
  clear(): void {
    for (const t of this.tickets.values()) {
      t.person.hidden = false;
      t.person.controlled = false;
    }
    this.fleet = [];
    this.tickets.clear();
    this.reservations.clear();
    this.lastDeparture.clear();
    this.log = [];
    this.time = this.checked = this.boardings = this.alightings = this.disruptions = 0;
  }
  reset(): void {
    this.clear();
    this.epoch = this.physics.life.epoch;
    if (this.stops.length < 2) return;
    const candidates = this.population.cars.filter((c) => c.kind === 'bus' || c.kind === 'coach');
    candidates.forEach((car, i) => {
      car.controlled = true;
      car.name = `Local ${i % 2 ? 'B' : 'A'} · ${car.kind === 'coach' ? 'regional coach' : 'city bus'}`;
      car.state = 'driving';
      car.speed = 6;
      const bus: Bus = {
        car,
        stop: -1,
        next: i % this.stops.length,
        state: 'closed',
        remaining: 0,
        retry: i * 1.3,
        reason: 'Preparing service',
        visits: 0,
      };
      this.fleet.push(bus);
    });
  }
  open(stop: BusStop): boolean {
    const owner = this.physics.cellOwner.get(stop.queues[0].supportCell!);
    return (
      owner !== undefined &&
      !!(this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner))?.anchored &&
      this.population.network.open(this.population.network.edges.get(stop.edge)!, false, 10.6) &&
      stop.queues.some((p) => this.population.civil?.network.available(p))
    );
  }
  book(person: Person, final: Place): Place | undefined {
    const old = this.tickets.get(person.id);
    if (old) return old.queue;
    const direct = Math.hypot(final.x - person.p.x, final.z - person.p.z);
    if (direct < 110 || !this.fleet.some((b) => b.state !== 'wreck')) return;
    const nearest = (p: { x: number; z: number }) =>
      this.stops
        .filter((s) => this.open(s))
        .sort(
          (a, b) =>
            Math.hypot(a.queues[0].x - p.x, a.queues[0].z - p.z) -
            Math.hypot(b.queues[0].x - p.x, b.queues[0].z - p.z),
        )[0];
    const origin = nearest(person.p),
      destination = nearest(final);
    if (!origin || !destination || origin === destination) return;
    const walking =
      Math.hypot(person.p.x - origin.queues[0].x, person.p.z - origin.queues[0].z) +
      Math.hypot(final.x - destination.queues[0].x, final.z - destination.queues[0].z);
    if (walking > direct - 45 || walking > 210) return;
    const queue = origin.queues.find(
      (p) =>
        this.population.civil!.network.available(p) &&
        this.reservations.acquire(p.id, person.id, this.time, 1, 600),
    );
    if (!queue) return;
    this.tickets.set(person.id, {
      person,
      origin,
      destination,
      queue,
      final,
      state: 'approaching',
      time: this.time,
      route: [],
    });
    this.record('Walking to reserved bus stop', undefined, person, origin);
    return queue;
  }
  waiting(person: Person): boolean {
    const t = this.tickets.get(person.id);
    if (!t) return false;
    if (!this.open(t.origin)) {
      this.cancel(person.id, 'Bus stop disrupted');
      return false;
    }
    if (
      t.state === 'approaching' &&
      person.p.distanceTo(new THREE.Vector3(t.queue.x, t.queue.y, t.queue.z)) < 0.6
    ) {
      t.state = 'waiting';
      t.time = this.time;
      this.record('Waiting for local bus', undefined, person, t.origin);
    }
    return true;
  }
  cancel(id: string, reason: string): void {
    const t = this.tickets.get(id);
    if (!t) return;
    this.tickets.delete(id);
    this.reservations.release(id);
    t.person.hidden = false;
    t.person.controlled = false;
    this.record(reason, t.bus, t.person);
    if (!['incapacitated', 'falling', 'flee'].includes(t.person.state))
      this.population.civil?.finishTransit(t.person, t.final, reason);
  }
  private passengers(bus: Bus): BusTicket[] {
    return [...this.tickets.values()].filter((t) => t.bus === bus);
  }
  private door(bus: Bus): THREE.Vector3 {
    return bus.car.p
      .clone()
      .add(
        new THREE.Vector3(bus.car.width / 2 + 0.4, 0.08, -bus.car.length / 2 + 1.15).applyQuaternion(
          bus.car.q,
        ),
      );
  }
  private dispatch(bus: Bus): void {
    const stop = this.stops[bus.next],
      car = bus.car;
    if (!this.open(stop)) {
      bus.next = (bus.next + 1) % this.stops.length;
      bus.retry = 3;
      bus.state = 'closed';
      bus.reason = 'Stop unavailable; selecting next open stop';
      return;
    }
    const route = this.population.plannedRoute(car, { ...stop.point, edge: stop.edge }, stop.from, false);
    if (!route.length) {
      bus.state = 'closed';
      bus.retry = 8;
      bus.reason = 'No safe road route to stop';
      return;
    }
    car.plan = route;
    car.state = 'driving';
    car.parkingYaw = Math.atan2(-stop.direction.x, -stop.direction.z);
    car.targetSpeed = 0;
    car.blockedFor = 0;
    bus.state = 'driving';
    bus.reason = `Serving ${stop.name}`;
    this.record('Departed for stop', bus, undefined, stop);
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) return;
    this.time += dt;
    this.checked += dt;
    for (const bus of this.fleet) {
      const car = bus.car;
      bus.retry -= dt;
      if (car.state === 'wreck' && bus.state !== 'wreck') {
        bus.state = 'wreck';
        this.disruptions++;
        car.doorOpen = 0;
        this.reservations.release(car.id);
        for (const t of this.passengers(bus)) {
          const p = t.person;
          p.hidden = false;
          p.p.copy(this.door(bus));
          p.previous.copy(p.p);
          p.state = 'incapacitated';
          p.velocity.copy(car.body.linvel());
          this.cancel(p.id, 'Bus damaged; original passenger awaiting assistance');
        }
        continue;
      }
      if (bus.state === 'wreck') continue;
      if (bus.state === 'closing') {
        car.doorOpen = Math.max(0, (car.doorOpen ?? 0) - dt * 2);
        if (car.doorOpen === 0) {
          bus.state = 'closed';
          bus.retry = 1;
          bus.next = (bus.stop + 1) % this.stops.length;
          this.lastDeparture.set(this.stops[bus.stop].id, this.time);
          this.reservations.release(car.id);
          this.record('Doors closed; rejoining traffic', bus, undefined, this.stops[bus.stop]);
        }
      }
      if (bus.state === 'closed' && bus.retry <= 0) this.dispatch(bus);
      if (bus.state === 'driving' && car.state === 'deployed') {
        const stop = this.stops[bus.next];
        if (car.p.distanceTo(new THREE.Vector3(stop.point.x, stop.point.y, stop.point.z)) > 1.5) {
          bus.state = 'closed';
          bus.retry = 4;
          continue;
        }
        if (!this.reservations.acquire(stop.id, car.id, this.time, 1, 60)) {
          car.state = 'waiting';
          car.timer = 2;
          bus.state = 'closed';
          bus.retry = 3;
          continue;
        }
        bus.stop = bus.next;
        bus.state = 'dwell';
        bus.remaining = 18;
        bus.visits++;
        bus.reason = 'Stopped; opening passenger door';
        this.population.audio.play('door', 0.8, car.p);
        this.record('Arrived at stop', bus, undefined, stop);
      }
      if (bus.state === 'driving' && car.blockedBy && car.actualSpeed < 0.1) {
        bus.remaining += dt;
        if (bus.remaining > 12) {
          bus.state = 'closed';
          bus.retry = 5;
          bus.remaining = 0;
          bus.reason = 'Road blocked; replanning service';
        }
      } else if (bus.state === 'driving') bus.remaining = 0;
      if (bus.state === 'dwell') {
        const stop = this.stops[bus.stop];
        bus.remaining -= dt;
        this.reservations.acquire(stop.id, car.id, this.time, 1, 60);
        car.doorOpen = Math.min(1, (car.doorOpen ?? 0) + dt * 2);
        const moving = this.passengers(bus).find((t) => t.state === 'boarding' || t.state === 'alighting');
        if (!moving && (car.doorOpen ?? 0) > 0.95) {
          const leaving = this.passengers(bus).find((t) => t.state === 'aboard' && t.destination === stop);
          if (leaving) {
            const door = this.door(bus);
            if (
              this.population.footClear(leaving.person, door) &&
              this.physics.ground(door, 0.35, 0.55, car.body)
            ) {
              leaving.state = 'alighting';
              leaving.person.hidden = false;
              leaving.person.p.copy(door);
              leaving.person.previous.copy(door);
              leaving.person.layer = 'street';
              leaving.person.detour = undefined;
              leaving.time = this.time;
              this.record('Alighting through bus door', bus, leaving.person, stop);
            }
          } else if (
            bus.remaining > 2 &&
            this.passengers(bus).length < (car.kind === 'coach' ? 28 : 24) &&
            this.open(stop)
          ) {
            const t = [...this.tickets.values()].find((t) => t.origin === stop && t.state === 'waiting');
            if (t) {
              t.bus = bus;
              t.state = 'boarding';
              t.time = this.time;
              t.person.controlled = true;
              t.person.detour = undefined;
              t.person.state = 'boarding';
            }
          }
        }
        if (
          bus.remaining <= 0 &&
          !this.passengers(bus).some((t) => t.state === 'boarding' || t.state === 'alighting') &&
          this.time - (this.lastDeparture.get(stop.id) ?? -Infinity) > 24
        ) {
          bus.state = 'closing';
        }
      }
    }
    for (const t of [...this.tickets.values()]) {
      const p = t.person;
      if (['incapacitated', 'falling', 'flee'].includes(p.state)) {
        this.cancel(p.id, 'Passenger interrupted by incident');
        continue;
      }
      if (t.state === 'aboard' && t.bus) {
        if (this.checked > 1 && !this.open(t.destination)) {
          const next = this.stops.find((s) => this.open(s));
          if (next) {
            t.destination = next;
            this.record('Destination stop disrupted; using an open stop', t.bus, p, next);
          }
        }
        p.previous.copy(p.p);
        p.p.copy(t.bus.car.p).add(new THREE.Vector3(0, 0.9, 0));
        p.hidden = true;
        p.controlled = true;
        p.state = 'riding bus';
        continue;
      }
      if (t.state === 'boarding' && t.bus) {
        const door = this.door(t.bus);
        p.previous.copy(p.p);
        p.state = 'boarding';
        this.population.walkTo(p, door, dt, 1.25);
        if (p.p.distanceTo(door) < 0.24) {
          t.state = 'aboard';
          p.hidden = true;
          p.state = 'riding bus';
          this.boardings++;
          this.reservations.release(p.id);
          this.record('Boarded original resident', t.bus, p, t.origin);
        } else if (this.time - t.time > 18) this.cancel(p.id, 'Door approach blocked; continue safely');
      }
      if (t.state === 'alighting' && t.bus) {
        p.previous.copy(p.p);
        p.state = 'alighting';
        const goal = t.destination.queues[0];
        this.population.walkTo(p, goal, dt, 1.25);
        if (p.p.distanceTo(new THREE.Vector3(goal.x, goal.y, goal.z)) < 0.25) {
          this.alightings++;
          this.cancel(p.id, 'Alighted; continuing original journey');
        } else if (this.time - t.time > 18)
          this.cancel(p.id, 'Alighting route blocked; selecting accessible continuation');
      }
      if ((t.state === 'waiting' || t.state === 'approaching') && this.time - t.time > 540)
        this.cancel(p.id, 'Bus service delayed; continue by accessible route');
    }
    if (this.checked > 1) {
      this.checked = 0;
      for (const t of [...this.tickets.values()])
        if (
          ['approaching', 'waiting'].includes(t.state) &&
          (!this.open(t.origin) || !this.open(t.destination))
        )
          this.cancel(t.person.id, 'Stop support or road disrupted');
    }
  }
  snapshot(): Record<string, number> {
    return {
      buses: this.fleet.length,
      busBoardings: this.boardings,
      busAlightings: this.alightings,
      busPassengers: [...this.tickets.values()].filter((t) => t.state === 'aboard').length,
      busWaiting: [...this.tickets.values()].filter((t) => t.state === 'waiting').length,
      busStopVisits: this.fleet.reduce((n, b) => n + b.visits, 0),
      busDisruptions: this.disruptions,
    };
  }
  inspect(): unknown {
    return {
      fleet: this.fleet.map((b) => ({
        id: b.car.id,
        state: b.state,
        reason: b.reason,
        position: b.car.p.toArray(),
        stop: this.stops[b.stop]?.name,
        next: this.stops[b.next]?.name,
        visits: b.visits,
        blocked: b.car.blockedBy,
      })),
      tickets: [...this.tickets.values()].map((t) => ({
        id: t.person.id,
        state: t.state,
        origin: t.origin.name,
        destination: t.destination.name,
        final: t.final.id,
      })),
      log: this.log,
    };
  }
}
